import { ReactNode, useMemo } from 'react';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { TextBody } from '@cbhq/cds-web/typography';

import { IdentityMultiContentLayoutDesktop } from './IdentityMultiContentLayoutDesktop';
import { IdentityMultiContentLayoutMobile } from './IdentityMultiContentLayoutMobile';
import { IdentityMultiContentLayoutProps } from './types';

type Props = IdentityMultiContentLayoutProps & {
  /**
   * @deprecated The layout has no dedicated subtitle — fold the copy into
   * `description` instead (pass a string, or an array of lines). Retained only for
   * backward compatibility while legacy call sites migrate (IDFE-3642); when set it
   * is prepended to `description` as an additional muted line. Slated for removal in
   * a future coordinated major.
   */
  subtitle?: string;
};

function foldSubtitle(
  subtitle: string | undefined,
  description: IdentityMultiContentLayoutProps['description'],
): IdentityMultiContentLayoutProps['description'] {
  if (!subtitle) return description;
  if (description == null) return subtitle;
  if (typeof description === 'string') return [subtitle, description];
  if (Array.isArray(description)) return [subtitle, ...description];
  // description is a ReactNode — render the subtitle as its own muted line above it
  // so it keeps a paragraph break. No current caller hits this path.
  return (
    <>
      <TextBody as="p" color="fgMuted" display="block">
        {subtitle}
      </TextBody>
      {description as ReactNode}
    </>
  );
}

/**
 * The canonical content-module layout for identity screens: arranges an optional
 * pictogram, `title`, `description`, `body`, `footer`, and `end` slot with the
 * correct responsive gaps, picking the mobile or desktop variant off the breakpoint.
 *
 * The deprecated `subtitle` prop is folded into `description` for backward
 * compatibility; new code should pass `description` directly.
 */
export function IdentityMultiContentLayout({ subtitle, description, ...props }: Props) {
  const { isPhone } = useBreakpoints();

  const mergedDescription = useMemo(
    () => foldSubtitle(subtitle, description),
    [subtitle, description],
  );

  return isPhone ? (
    <IdentityMultiContentLayoutMobile {...props} description={mergedDescription} />
  ) : (
    <IdentityMultiContentLayoutDesktop {...props} description={mergedDescription} />
  );
}
